import type { MapData } from '@/types/map-data';
import type { GhostName } from '@/types/ghost';

type Direction = 'left' | 'right' | 'up' | 'down' | 'none';

interface Neighbor {
  readonly x: number;
  readonly y: number;
  readonly direction: Direction;
}

function tileKey(x: number, y: number): string {
  return `${x},${y}`;
}

const DIR_DELTAS: Readonly<Record<string, { dx: number; dy: number }>> = {
  left: { dx: -1, dy: 0 },
  right: { dx: 1, dy: 0 },
  up: { dx: 0, dy: -1 },
  down: { dx: 0, dy: 1 },
};

/**
 * Precomputed maze graph for fast BFS-based pathfinding.
 * Built once per level load. Ghosts query it at each intersection
 * to determine the optimal direction toward their target.
 *
 * Uses BFS on an unweighted adjacency list (equivalent to A*
 * with uniform edge weights). Distance maps for fixed targets
 * (base exit, scatter corners) are precomputed at construction.
 * Dynamic targets (pac-man) are computed lazily and cached until
 * the target tile changes.
 */
export class MazeGraph {
  private readonly adjacency: Map<string, readonly Neighbor[]> = new Map();
  private readonly mapWidth: number;
  private readonly mapHeight: number;

  /** Precomputed distance map from base exit (for returning ghosts). */
  private readonly baseReturnDistances: ReadonlyMap<string, number>;

  /** Precomputed distance maps from scatter corner targets per ghost. */
  private readonly scatterDistances: ReadonlyMap<string, ReadonlyMap<string, number>>;

  /** Frame-cached BFS for dynamic target (pac-man). */
  private cachedTargetKey = '';
  private cachedDistances: ReadonlyMap<string, number> | null = null;

  constructor(
    mapData: MapData,
    exitTargetX: number,
    exitTargetY: number,
    scatterTargets: ReadonlyMap<GhostName, { readonly x: number; readonly y: number }>,
  ) {
    this.mapWidth = mapData.width;
    this.mapHeight = mapData.height;

    this.buildGraph(mapData);

    // Precompute distance map for base return
    this.baseReturnDistances = this.bfs(exitTargetX, exitTargetY);

    // Precompute distance maps for each ghost's scatter corner
    const scatterMaps = new Map<string, ReadonlyMap<string, number>>();
    for (const [name, target] of scatterTargets) {
      const walkable = this.findNearestWalkable(target.x, target.y);
      scatterMaps.set(name, this.bfs(walkable.x, walkable.y));
    }
    this.scatterDistances = scatterMaps;
  }

  /** Precomputed BFS distance from a tile to the ghost base exit. */
  getDistanceToBase(tileX: number, tileY: number): number {
    return this.baseReturnDistances.get(tileKey(tileX, tileY)) ?? Infinity;
  }

  /** Precomputed BFS distance from a tile to a ghost's scatter corner. */
  getScatterDistance(ghostName: GhostName, tileX: number, tileY: number): number {
    const distances = this.scatterDistances.get(ghostName);
    if (!distances) return Infinity;
    return distances.get(tileKey(tileX, tileY)) ?? Infinity;
  }

  /**
   * BFS distance from a tile to a dynamic target (pac-man).
   * Cached per target tile; recomputed when target moves.
   */
  getDistanceToTarget(
    tileX: number,
    tileY: number,
    targetX: number,
    targetY: number,
  ): number {
    const tk = tileKey(targetX, targetY);
    if (tk !== this.cachedTargetKey || !this.cachedDistances) {
      this.cachedDistances = this.bfs(targetX, targetY);
      this.cachedTargetKey = tk;
    }
    return this.cachedDistances.get(tileKey(tileX, tileY)) ?? Infinity;
  }

  /**
   * Reconstruct the BFS-optimal path from a start tile to the base exit.
   */
  getPathToBase(startX: number, startY: number): Array<{ x: number; y: number }> {
    return this.reconstructPath(startX, startY, this.baseReturnDistances);
  }

  /** Reconstruct path from a start tile toward a ghost's scatter corner. */
  getPathToScatter(ghostName: GhostName, startX: number, startY: number): Array<{ x: number; y: number }> {
    const distances = this.scatterDistances.get(ghostName);
    if (!distances) return [];
    return this.reconstructPath(startX, startY, distances);
  }

  /** Reconstruct path from a start tile toward a dynamic target (pac-man). */
  getPathToTarget(startX: number, startY: number, targetX: number, targetY: number): Array<{ x: number; y: number }> {
    const tk = tileKey(targetX, targetY);
    if (tk !== this.cachedTargetKey || !this.cachedDistances) {
      this.cachedDistances = this.bfs(targetX, targetY);
      this.cachedTargetKey = tk;
    }
    return this.reconstructPath(startX, startY, this.cachedDistances);
  }

  /**
   * Reconstruct a BFS path by following the distance gradient downhill.
   */
  private reconstructPath(
    startX: number,
    startY: number,
    distances: ReadonlyMap<string, number>,
  ): Array<{ x: number; y: number }> {
    const path: Array<{ x: number; y: number }> = [];
    let cx = startX;
    let cy = startY;
    const maxSteps = this.mapWidth * this.mapHeight; // safety limit

    for (let step = 0; step < maxSteps; step++) {
      path.push({ x: cx, y: cy });
      const currentDist = distances.get(tileKey(cx, cy));
      if (currentDist === undefined || currentDist === 0) break;

      const neighbors = this.adjacency.get(tileKey(cx, cy));
      if (!neighbors) break;

      let bestDist = currentDist;
      let bestX = cx;
      let bestY = cy;
      for (const n of neighbors) {
        const nd = distances.get(tileKey(n.x, n.y));
        if (nd !== undefined && nd < bestDist) {
          bestDist = nd;
          bestX = n.x;
          bestY = n.y;
        }
      }
      if (bestX === cx && bestY === cy) break; // stuck
      cx = bestX;
      cy = bestY;
    }

    return path;
  }

  /**
   * Walk from a starting position in the given direction, following corridors,
   * until reaching a junction (3+ neighbors), dead end, or wall.
   * Returns tiles traversed (excluding the starting tile).
   */
  getCorridorTiles(
    startX: number,
    startY: number,
    direction: 'left' | 'right' | 'up' | 'down',
  ): Array<{ x: number; y: number }> {
    const tiles: Array<{ x: number; y: number }> = [];
    let cx = startX;
    let cy = startY;
    const d = DIR_DELTAS[direction];
    if (!d) return tiles;
    let dx = d.dx;
    let dy = d.dy;
    const maxSteps = this.mapWidth + this.mapHeight;

    for (let step = 0; step < maxSteps; step++) {
      let nx = cx + dx;
      const ny = cy + dy;

      // Handle tunnel wrapping
      if (nx < 0) nx = this.mapWidth - 1;
      if (nx >= this.mapWidth) nx = 0;

      if (!this.adjacency.has(tileKey(nx, ny))) {
        break; // hit a wall
      }

      const fromX = cx;
      const fromY = cy;
      cx = nx;
      cy = ny;
      tiles.push({ x: cx, y: cy });

      const neighbors = this.adjacency.get(tileKey(cx, cy));
      if (!neighbors || neighbors.length >= 3 || neighbors.length <= 1) {
        break; // junction, dead end, or isolated
      }

      // Corridor (2 neighbors) - continue in the non-backward direction
      const forward = neighbors.find(n => !(n.x === fromX && n.y === fromY));
      if (!forward) break;
      dx = forward.x - cx;
      dy = forward.y - cy;
    }

    return tiles;
  }

  /**
   * Find the next junction (3+ neighbors) walking from a position in a direction.
   * Returns the junction tile, or null if a dead-end / wall is hit first.
   */
  findNextJunction(
    startX: number,
    startY: number,
    direction: 'left' | 'right' | 'up' | 'down',
  ): { x: number; y: number } | null {
    const tiles = this.getCorridorTiles(startX, startY, direction);
    if (tiles.length === 0) return null;

    const last = tiles[tiles.length - 1]!;
    const neighbors = this.adjacency.get(tileKey(last.x, last.y));
    if (neighbors && neighbors.length >= 3) {
      return last;
    }
    return null;
  }

  /** Clear the dynamic target cache. Call when pac-man changes tile. */
  invalidateCache(): void {
    this.cachedTargetKey = '';
    this.cachedDistances = null;
  }

  // ---- Graph Construction ----

  private buildGraph(mapData: MapData): void {
    for (let y = 0; y < mapData.height; y++) {
      for (let x = 0; x < mapData.width; x++) {
        if (!this.isGhostWalkableTile(mapData, x, y)) continue;

        const neighbors: Neighbor[] = [];

        // Up
        if (y > 0 && this.isGhostWalkableTile(mapData, x, y - 1)) {
          neighbors.push({ x, y: y - 1, direction: 'up' });
        }

        // Down
        if (y < mapData.height - 1 && this.isGhostWalkableTile(mapData, x, y + 1)) {
          neighbors.push({ x, y: y + 1, direction: 'down' });
        }

        // Left
        if (x > 0 && this.isGhostWalkableTile(mapData, x - 1, y)) {
          neighbors.push({ x: x - 1, y, direction: 'left' });
        } else if (x === 0 && this.isTunnelRow(mapData, y)) {
          if (this.isGhostWalkableTile(mapData, mapData.width - 1, y)) {
            neighbors.push({ x: mapData.width - 1, y, direction: 'left' });
          }
        }

        // Right
        if (x < mapData.width - 1 && this.isGhostWalkableTile(mapData, x + 1, y)) {
          neighbors.push({ x: x + 1, y, direction: 'right' });
        } else if (x === mapData.width - 1 && this.isTunnelRow(mapData, y)) {
          if (this.isGhostWalkableTile(mapData, 0, y)) {
            neighbors.push({ x: 0, y, direction: 'right' });
          }
        }

        this.adjacency.set(tileKey(x, y), neighbors);
      }
    }
  }

  /** Walkable for released, non-returning ghosts (excludes walls and ghost_base). */
  private isGhostWalkableTile(mapData: MapData, x: number, y: number): boolean {
    if (x < 0 || x >= mapData.width || y < 0 || y >= mapData.height) return false;
    const row = mapData.tiles[y];
    if (!row) return false;
    const tile = row[x];
    return tile !== 'wall' && tile !== 'ghost_base';
  }

  private isTunnelRow(mapData: MapData, y: number): boolean {
    return mapData.tunnelPairs.some(
      (pair) => pair.entry.y === y || pair.exit.y === y,
    );
  }

  /**
   * BFS from a source tile. Returns distance (in tiles) from every
   * reachable tile to the source.
   */
  private bfs(sourceX: number, sourceY: number): ReadonlyMap<string, number> {
    const distances = new Map<string, number>();
    const queue: Array<{ x: number; y: number }> = [];

    const startKey = tileKey(sourceX, sourceY);

    // If source itself is not in the adjacency list (wall/ghost_base),
    // try the nearest walkable tile.
    if (!this.adjacency.has(startKey)) {
      const nearest = this.findNearestWalkable(sourceX, sourceY);
      const nearestKey = tileKey(nearest.x, nearest.y);
      if (!this.adjacency.has(nearestKey)) return distances; // unreachable
      distances.set(nearestKey, 0);
      queue.push(nearest);
    } else {
      distances.set(startKey, 0);
      queue.push({ x: sourceX, y: sourceY });
    }

    let head = 0;
    while (head < queue.length) {
      const current = queue[head++]!;
      const currentKey = tileKey(current.x, current.y);
      const currentDist = distances.get(currentKey)!;

      const neighbors = this.adjacency.get(currentKey);
      if (!neighbors) continue;

      for (const neighbor of neighbors) {
        const neighborKey = tileKey(neighbor.x, neighbor.y);
        if (!distances.has(neighborKey)) {
          distances.set(neighborKey, currentDist + 1);
          queue.push({ x: neighbor.x, y: neighbor.y });
        }
      }
    }

    return distances;
  }

  /**
   * Find the nearest walkable tile to a given coordinate.
   * Used for scatter targets that may land on walls.
   */
  private findNearestWalkable(x: number, y: number): { x: number; y: number } {
    if (this.adjacency.has(tileKey(x, y))) return { x, y };

    const maxRadius = Math.max(this.mapWidth, this.mapHeight);
    for (let radius = 1; radius <= maxRadius; radius++) {
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          if (Math.abs(dx) !== radius && Math.abs(dy) !== radius) continue;
          const nx = x + dx;
          const ny = y + dy;
          if (this.adjacency.has(tileKey(nx, ny))) {
            return { x: nx, y: ny };
          }
        }
      }
    }

    return { x, y }; // fallback (should not happen)
  }
}
